import React, { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  AlertCircle,
  Download,
  Clock,
} from "lucide-react";
import { timestamp2string } from "../helpers";
import { renderQuota } from "../helpers/render";

/**
 * 最近日志组件
 * 显示日志列表，支持分页和筛选
 */
const RecentLogs = ({
  data,
  loading = false,
  compact = false,
  onExportCSV,
}) => {
  // 从data中提取logs数据，处理undefined情况
  const logs = data?.logs || [];

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25; // 增加每页显示数量，充分利用屏幕空间

  // 计算总页数
  const totalPages = Math.ceil(logs.length / itemsPerPage);

  // 获取页码数组
  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 7; // 增加显示的页码按钮数量

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // 获取模型徽章颜色
  const getModelBadgeColor = (modelName) => {
    const colorMap = {
      "gpt-4":
        "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700",
      "gpt-4-turbo":
        "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700",
      "gpt-3.5":
        "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700",
      claude:
        "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700",
      default:
        "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700",
    };

    for (const [key, value] of Object.entries(colorMap)) {
      if (modelName.toLowerCase().includes(key)) {
        return value;
      }
    }
    return colorMap.default;
  };

  // 计算当前页显示的日志
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const visibleLogs = logs.slice(startIndex, endIndex);

  return (
    <div className="w-full relative">
      {/* 顶部标题栏 - 简化版本，因为标题已经在折叠触发器中 */}
      <div className="relative z-10 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
        <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
          <span className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg">
            {visibleLogs.length > 0
              ? `${startIndex + 1}-${Math.min(endIndex, logs.length)} / ${
                  logs.length
                } 条记录`
              : "暂无记录"}
          </span>
          {onExportCSV && logs.length > 0 && (
            <>
              <span className="text-[11px] sm:text-xs text-gray-400 dark:text-gray-500 text-right">
                由于上游限制，仅支持查看近 1000 条调用记录
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={onExportCSV}
                className="h-8 sm:h-9 px-2 sm:px-4 text-gray-600 dark:text-gray-400 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-600 dark:hover:text-green-400 hover:border-green-200 dark:hover:border-green-700 transition-colors duration-200">
                <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">导出 CSV</span>
                <span className="sm:hidden">导出</span>
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="relative z-10">
        {loading ? (
          <div className="p-6 space-y-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton
                key={i}
                className="h-14 w-full bg-gray-200 dark:bg-gray-700 rounded-lg"
              />
            ))}
          </div>
        ) : visibleLogs.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full mb-6">
              <AlertCircle className="h-10 w-10 text-orange-500" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              暂无日志记录
            </h4>
            <p className="text-gray-600 dark:text-gray-400">
              请先查询API令牌以获取调用记录
            </p>
          </div>
        ) : (
          <>
            {/* 桌面端表格视图 */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-200 dark:border-gray-700 hover:bg-transparent bg-gray-50/50 dark:bg-gray-800/50">
                    <TableHead className="text-sm font-semibold text-gray-700 dark:text-gray-300 py-4 px-6 w-48">
                      调用时间
                    </TableHead>
                    <TableHead className="text-sm font-semibold text-gray-700 dark:text-gray-300 py-4 px-6 w-56">
                      模型名称
                    </TableHead>
                    <TableHead className="text-sm font-semibold text-gray-700 dark:text-gray-300 py-4 px-6 text-center w-32">
                      提示Tokens
                    </TableHead>
                    <TableHead className="text-sm font-semibold text-gray-700 dark:text-gray-300 py-4 px-6 text-center w-32">
                      补全Tokens
                    </TableHead>
                    <TableHead className="text-sm font-semibold text-gray-700 dark:text-gray-300 py-4 px-6 text-center w-32">
                      响应时间
                    </TableHead>
                    <TableHead className="text-sm font-semibold text-gray-700 dark:text-gray-300 py-4 px-6 text-right w-32">
                      费用
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleLogs.map((log, index) => (
                    <TableRow
                      key={index}
                      className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-200">
                      <TableCell className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                            {timestamp2string(log.created_at)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-6">
                        <Badge
                          variant="secondary"
                          className={`text-sm font-medium px-3 py-1 ${getModelBadgeColor(
                            log.model_name
                          )}`}>
                          {log.model_name}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-4 px-6 text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {(log.prompt_tokens || 0).toLocaleString()}
                          </span>
                          {log.inputRatioPrice &&
                            log.inputRatioPrice !== "0" && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                ${log.inputRatioPrice}/1M
                              </span>
                            )}
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-6 text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {(log.completion_tokens || 0).toLocaleString()}
                          </span>
                          {log.completionRatioPrice &&
                            log.completionRatioPrice !== "0" && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                ${log.completionRatioPrice}/1M
                              </span>
                            )}
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-6 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {log.use_time || "0"}s
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-6 text-right">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {renderQuota(log.quota, 5)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* 移动端卡片视图 */}
            <div className="md:hidden space-y-3 p-4">
              {visibleLogs.map((log, index) => (
                <Card
                  key={index}
                  className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {/* 时间和模型 */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {timestamp2string(log.created_at)}
                          </span>
                        </div>
                        <Badge
                          variant="secondary"
                          className={`text-xs font-medium px-2 py-0.5 ${getModelBadgeColor(
                            log.model_name
                          )}`}>
                          {log.model_name}
                        </Badge>
                      </div>

                      {/* Token信息 */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            提示词
                          </div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {(log.prompt_tokens || 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            补全词
                          </div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {(log.completion_tokens || 0).toLocaleString()}
                          </div>
                        </div>
                      </div>

                      {/* 响应时间和消耗 */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {log.use_time || "0"}s
                          </span>
                        </div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {renderQuota(log.quota, 5)}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* 分页控制 */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 order-2 sm:order-1">
                  <span>每页 {itemsPerPage} 条</span>
                  <span className="hidden sm:inline">•</span>
                  <span>共 {logs.length} 条记录</span>
                </div>

                <div className="flex items-center gap-1 sm:gap-2 order-1 sm:order-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="h-8 w-8 sm:h-9 sm:w-9 p-0 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
                    <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>

                  {/* 页码按钮 */}
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    {getPageNumbers().map((pageNum, index) =>
                      pageNum === "..." ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 sm:px-2 text-gray-400 text-xs sm:text-sm">
                          ...
                        </span>
                      ) : (
                        <Button
                          key={pageNum}
                          variant={
                            currentPage === pageNum ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className={`h-8 w-8 sm:h-9 sm:w-9 p-0 text-xs sm:text-sm ${
                            currentPage === pageNum
                              ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
                              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                          }`}>
                          {pageNum}
                        </Button>
                      )
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 sm:h-9 sm:w-9 p-0 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
                    <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RecentLogs;
